import React, { useState, useEffect } from 'react';
import '../Assests/Sidebar.css';
import Overview from './Overview';
import LeaveTracker from '../Components/Leave_Tracker';
import Attendance from '../Components/Attend_shift';
import RegisterEmployee from '../Components/RegisterEmp';
import ProjectTask from "./ProjectTask";
import { supabase } from '../Db_Connections/db_connection';

// ✅ NEW IMPORT: Dashboard API
import { addDashboardPost } from '../Db_Connections/dashboardAPi';

const Sidebar = ({ auth, onLogout }) => {
  const [activePage, setActivePage] = useState('home');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);

  // Notification Badge State
  const [pendingCount, setPendingCount] = useState(0);

  // Announcement Form State
  const [showAnnouncementModal, setShowAnnouncementModal] = useState(false);
  const [announcementType, setAnnouncementType] = useState('Announcement');
  const [announcementTitle, setAnnouncementTitle] = useState('');
  const [announcementMessage, setAnnouncementMessage] = useState('');
  const [announcementError, setAnnouncementError] = useState('');

  // Check Admin Role
  const isAdmin = auth?.role === 'Admin';

  // --- FETCH PENDING REQUESTS COUNT (For Badge) ---
  useEffect(() => {
    const fetchPendingCount = async () => {
      if (isAdmin) {
        const { count, error } = await supabase
          .from('leaves')
          .select('*', { count: 'exact', head: true })
          .eq('status', 'Pending');

        if (!error) {
          setPendingCount(count || 0);
        }
      }
    };

    fetchPendingCount();
    console.count("Sidebar fetchPendingCount");
    const interval = setInterval(fetchPendingCount, 600000); // Auto refresh every 10 min
    return () => clearInterval(interval);
  }, [isAdmin]);

  const navItems = [
    { id: 'home', label: 'Home', icon: 'home' },
    { id: 'leave', label: 'Leave', icon: 'leave' },
    { id: 'attendanceTracker', label: 'Attendance', icon: 'calendar' },
    { id: 'Project & Task', label: 'Project & Task', icon: 'ticket' },
   
  ];

  const renderIcon = (iconType) => {
    const icons = {
      home: <svg viewBox="0 0 24 24" fill="none"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /><polyline points="9 22 9 12 15 12 15 22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>,
      leave: <svg viewBox="0 0 24 24" fill="none"><rect x="3" y="4" width="18" height="18" rx="2" ry="2" stroke="currentColor" strokeWidth="2" /><line x1="16" y1="2" x2="16" y2="6" stroke="currentColor" strokeWidth="2" /><line x1="8" y1="2" x2="8" y2="6" stroke="currentColor" strokeWidth="2" /><line x1="3" y1="10" x2="21" y2="10" stroke="currentColor" strokeWidth="2" /><polyline points="9 15 11 17 15 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>,
      calendar: <svg viewBox="0 0 24 24" fill="none"><rect x="3" y="4" width="18" height="18" rx="2" ry="2" stroke="currentColor" strokeWidth="2" /><line x1="16" y1="2" x2="16" y2="6" stroke="currentColor" strokeWidth="2" /><line x1="8" y1="2" x2="8" y2="6" stroke="currentColor" strokeWidth="2" /><line x1="3" y1="10" x2="21" y2="10" stroke="currentColor" strokeWidth="2" /></svg>,
    };
    return icons[iconType] || icons.home;
  };

  const renderPageContent = () => {
    switch (activePage) {
      case 'home': return <Overview />;
      case 'leave': return <LeaveTracker />;
      case 'attendanceTracker': return <Attendance />;
      case 'Project & Task': return <ProjectTask />;
      case 'registerEmployee': return isAdmin ? <RegisterEmployee onBack={() => setActivePage('home')} /> : null;
      default: return <Overview />;
    }
  };

  // ========== MODAL HANDLERS ==========

  const openAddModal = () => setShowAddModal(true);
  const closeAddModal = () => setShowAddModal(false);

  const handleAddAction = (action) => {
    if (action === 'register-employee') {
      closeAddModal();
      setActivePage('registerEmployee');
    }
    if (action === 'dashboard-announcement') {
      closeAddModal();
      setAnnouncementType('Announcement');
      setAnnouncementTitle('');
      setAnnouncementMessage('');
      setAnnouncementError('');
      setShowAnnouncementModal(true);
    }
  };

  const closeAnnouncementModal = () => {
    setShowAnnouncementModal(false);
    setAnnouncementError('');
  };

  // ========== SUBMIT ANNOUNCEMENT (DB SAVE) ==========
  const handleAnnouncementSubmit = async (e) => {
    e.preventDefault();
    if (!announcementTitle.trim()) {
      setAnnouncementError('Title is required');
      return;
    }

    // ✅ DB Connection Logic
    const { error } = await addDashboardPost({
      type: announcementType,
      title: announcementTitle,
      message: announcementMessage,
      // Agar 'Quick Link' hai to message ko hi URL maan lo
      link_url: announcementType === 'Quick Link' ? announcementMessage : null,
      // Agar 'Holiday' hai to aaj ki date save kar lo (Ya future me date picker laga lena)
      event_date: announcementType === 'Holiday' ? new Date().toISOString().split('T')[0] : null
    });

    if (error) {
      setAnnouncementError('Error saving: ' + error.message);
    } else {
      alert('Post added successfully!');
      setShowAnnouncementModal(false);
      setAnnouncementTitle('');
      setAnnouncementMessage('');
      setAnnouncementError('');

      // Page reload to show new data on Dashboard
      window.location.reload();
    }
  };

  // ========== MENU HANDLERS ==========
  const toggleUserMenu = () => setShowUserMenu((prev) => !prev);
  const handleLogoutClick = () => { setShowUserMenu(false); onLogout?.(); };

  return (
    <div className="app-wrapper">
      <header className="top-header">
        <div className="header-left">
          <div className="header-logo">
            <svg viewBox="0 0 40 40" fill="none"><path d="M20 8L28 14V26L20 32L12 26V14L20 8Z" fill="#4A9EFF" /><path d="M20 8L28 14L20 20L12 14L20 8Z" fill="#64B5F6" /><path d="M12 14V26L20 32V20L12 14Z" fill="#2196F3" /><path d="M28 14V26L20 32V20L28 14Z" fill="#1976D2" /></svg>
          </div>
          <nav className="header-nav">
            {/* <a href='overview' className="header-link active">My Space</a>
            {/* <a href="#team" className="header-link">Team</a>
            <a href="#organization" className="header-link">Organization</a> */} 
          </nav>
        </div>

        <div className="header-right">
          {isAdmin && (
            <button className="header-icon-btn add-btn" onClick={openAddModal} title="Quick Actions">
              <svg viewBox="0 0 24 24" fill="none"><line x1="12" y1="5" x2="12" y2="19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /><line x1="5" y1="12" x2="19" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
            </button>
          )}
          <button className="header-icon-btn">
            <svg viewBox="0 0 24 24" fill="none"><circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2" /><path d="m21 21-4.35-4.35" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
          </button>
          <button className="header-icon-btn">
            <svg viewBox="0 0 24 24" fill="none"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /><path d="M13.73 21a2 2 0 0 1-3.46 0" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
          </button>
          <div className="header-user-menu">
            <button className="header-icon-btn" onClick={toggleUserMenu}>
              <svg viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33A1.65 1.65 0 0 0 14 21v.09A2 2 0 0 1 12 23a2 2 0 0 1-2-1.91V21a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 5 15.4 1.65 1.65 0 0 0 3.49 14H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 8 4.6 1.65 1.65 0 0 0 9 3.09V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09A1.65 1.65 0 0 0 14 4.6a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06A1.65 1.65 0 0 0 18.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09A1.65 1.65 0 0 0 19.4 15Z" stroke="currentColor" strokeWidth="2" /></svg>
            </button>
            {showUserMenu && (
              <div className="header-user-dropdown">
                <div style={{ padding: '10px 15px', borderBottom: '1px solid #eee', fontSize: '13px', fontWeight: 'bold' }}>
                  {auth?.first_name} {auth?.last_name} <br />
                  <span style={{ fontSize: '11px', color: '#666', fontWeight: 'normal' }}>{auth?.role}</span>
                </div>
                <button className="dropdown-item dropdown-item-logout" onClick={handleLogoutClick}>Logout</button>
              </div>
            )}
          </div>
        </div>
      </header>

      <nav className="sidebar">
        <div className="nav-items">
          {navItems.map((item) => (
            <button
              key={item.id}
              className={`nav-item ${activePage === item.id ? 'active' : ''}`}
              onClick={() => setActivePage(item.id)}
              style={{ position: 'relative' }}
            >
              <div className="nav-icon">{renderIcon(item.icon)}</div>
              <span className="nav-label">{item.label}</span>
              {isAdmin && item.id === 'leave' && pendingCount > 0 && (
                <span className="sidebar-badge">{pendingCount > 9 ? '9+' : pendingCount}</span>
              )}
            </button>
          ))}
        </div>
      </nav>

      <div className="main-content-wrapper">{renderPageContent()}</div>

      {showAddModal && isAdmin && (
        <div className="add-modal-overlay" onClick={closeAddModal}>
          <div className="add-modal" onClick={(e) => e.stopPropagation()}>
            <div className="add-modal-header">
              <h3>Quick Actions</h3>
              <button className="add-modal-close" type="button" onClick={closeAddModal}>×</button>
            </div>
            <div className="add-modal-options">
              <button className="add-option" type="button" onClick={() => handleAddAction('register-employee')}>
                <span className="add-option-icon">👤</span>
                <div><h4>Register New Employee</h4><p>Create profile for a new joiner</p></div>
              </button>
              <button className="add-option" type="button" onClick={() => handleAddAction('dashboard-announcement')}>
                <span className="add-option-icon">📢</span>
                <div><h4>Dashboard Announcement</h4><p>Post an update to the dashboard</p></div>
              </button>
            </div>
          </div>
        </div>
      )}

      {showAnnouncementModal && isAdmin && (
        <div className="add-modal-overlay" onClick={closeAnnouncementModal}>
          <div className="add-modal" onClick={(e) => e.stopPropagation()}>
            <div className="add-modal-header">
              <h3>New Post</h3>
              <button className="add-modal-close" type="button" onClick={closeAnnouncementModal}>×</button>
            </div>
            <form onSubmit={handleAnnouncementSubmit}>
              <div className="form-group">
                <label className="form-label">Category</label>
                <select className="form-input" value={announcementType} onChange={(e) => setAnnouncementType(e.target.value)}>
                  <option value="Announcement">General Announcement</option>
                  <option value="Holiday">Upcoming Holiday</option>
                  <option value="Quick Link">Quick Link</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Title / Name <span className="req">*</span></label>
                <input type="text" className="form-input" value={announcementTitle} onChange={(e) => { setAnnouncementTitle(e.target.value); setAnnouncementError(''); }} placeholder={announcementType === 'Holiday' ? "e.g. Diwali Vacation" : "e.g. Server Maintenance"} />
                {announcementError && <p className="error-text">{announcementError}</p>}
              </div>
              <div className="form-group">
                <label className="form-label">{announcementType === 'Quick Link' ? 'URL / Link' : 'Message / Details'}</label>
                <textarea className="form-input announcement-textarea" value={announcementMessage} onChange={(e) => setAnnouncementMessage(e.target.value)} placeholder={announcementType === 'Quick Link' ? "https://example.com" : "Details about the post..."} />
              </div>
              <div className="add-modal-footer">
                <button type="button" className="btn-secondary" onClick={closeAnnouncementModal}>Cancel</button>
                <button type="submit" className="btn-primary">Post Update</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Sidebar;